import React, { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import QRCode from 'qrcode.react';
import styles from './Form.module.scss';
import './customStyle.css';
import { useCourseContent } from '@hooks/useCourseContent';
import Button from '@src/components/button/Button';
import iconSrc from './assets/icon.png';
import IconOutline from '@src/assets/icons/outline.svg?react';
import IconHoverOutline from '@src/assets/icons/hover_outline.svg?react';
import InfoCircleIcon from '@src/assets/icons/infocircle.svg?react';
import OutlineIcon from 'src/assets/icons/outline.svg?react';
import TicketIcon from 'src/assets/icons/ticket.svg?react';
import WarningIcon from 'src/assets/icons/red-warning.svg?react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { isHomeEDApp, ExitTicketExpirationTime, ResearchExpirationTime } from '../../../../constants';
import { useNextContent } from '../../useNextContent';
import { useUser } from '@hooks/useUser';
import { useCreateFeedbackFormMutation, FeedbackFormContent, useCancelFeedbackFormMutation, ContentType, Quiz } from '@api/graphql-codegen';
import { useAnalytics, Events, useToast, useExitTicketLocalStorage } from '@hooks';
import { ExtendedQuiz, FeedbackForm, SurveyTypes } from '@types';
import GenerateExitTicketLinkModal from '../../exitticket-modal/GenerateExitTicketLinkModal';
import Spinner from '@src/components/spinner/Spinner';
import { isNumber } from "@src/utils/validation";
import { logger } from '../../../../utils/logger';

type Props = {
  formData?: string | null;
  jsonFormData: FeedbackForm;
  name: string;
  goBack?: () => void;
  setComplete: () => void;
  showMarkCompleteModal: boolean;
};

const createURL = (id: string) => {
  return `${window.location.origin}/ff/${id}`;
};

function FormGenerateContent(props: Props) {
  const { jsonFormData, name, formData } = props;
  const { 
    program,
    lesson,
    currentUnit,
    currentHeaUnit,
    exitTickets,
    setExitTickets,
    content: contentValue,
    isStudentQuiz,
    currentQuiz,
    setCurrentQuiz,
    myQuizzes,
    setMyQuizzes,
  } = useCourseContent();
  const { hasNextContent } = useNextContent();
  const [user] = useUser();
  const [feedbackForm, setFeedbackForm] = useState<FeedbackFormContent | undefined>();
  const [timeLeft, setTimeLeft] = useState<number>(ResearchExpirationTime * 60);
  // const [hideCanvas, setHideCanvas] = useState(true);
  // const [showNotify, setShowNotify] = useState(false);
  const [copyLinkHovered, setCopyLinkHovered] = useState(false);
  const [isLinkHovered, setIsLinkHovered] = useState(false);
  const [isQrHovered, setIsQrHovered] = useState(false);
  const [isActiveHovered, setIsActiveHovered] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRememberNotify, setShowRememberNotify] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [regenerate, setRegenerate] = useState(false);
  const containerRef = React.useRef(null);


  const { trackEvent } = useAnalytics();
  const { successToast } = useToast();
  const { getFormId, setFormId, removeFormId } = useExitTicketLocalStorage();

  const exitTicket = useMemo(() => (jsonFormData ? true : false), [jsonFormData]);
  const formTypeExitTicket = useMemo(() => (Number(jsonFormData?.surveyType) === 1 ? true : false), [jsonFormData]);
  const formTypePreProgram = useMemo(() => (Number(jsonFormData?.surveyType) === 3 ? true : false), [jsonFormData]);
  const Expiration_time = useMemo(() => ((Number(jsonFormData?.surveyType) === 2 ? ResearchExpirationTime : ExitTicketExpirationTime) * 60), [jsonFormData])
  let countDownTimer = useRef<NodeJS.Timeout | null>(null);

  const exitTicketActive = useMemo(() => {
    let tickets = exitTickets.filter(ticket => ticket.lessonNum === lesson?.number)
    return tickets.length > 0 ? tickets[0] : null
  }, [exitTickets, lesson?.number])

  const [cancelFeedbackForm, { loading: canceling }] = useCancelFeedbackFormMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
    onCompleted(data) {

      if (data.cancelFeedbackForm && !regenerate) {
        removeFormId(lesson?.id)
        setRegenerate(false)
        const tickets = exitTickets.map(ticket => {
          if (lesson?.number === ticket.lessonNum) {
            return {
              ...ticket,
              status: false,
              exitTicketCount: 0,
              expiryMinutes: 0,
              copyLink: '',
              code: '',
            }
          }
          return ticket
        })
        setExitTickets(tickets);
        setFeedbackForm(undefined);
        if (isStudentQuiz) {
          const updatedQuiz = {
            ...currentQuiz,
            feedbackForm: undefined,
            expiryMinutes: 0,
            status: false,
          }
          setCurrentQuiz(updatedQuiz as ExtendedQuiz)
          const updateQuizzes = myQuizzes.map((quiz: any, i: number) => {
            if (quiz.id === updatedQuiz?.id) {
              return {
                ...quiz,
                feedbackForm: undefined,
                expiryMinutes: 0,
                status: false,
              }
            } else {
              return quiz
            }
          })
          setMyQuizzes(updateQuizzes);
        }
      }
    }
  })

  useEffect(() => {
    if (timeLeft <= 0) {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current)
        countDownTimer.current = null;
      }
    }
  }, [timeLeft]);

  useEffect(() => {
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current)
        countDownTimer.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (isStudentQuiz && contentValue?.type === ContentType.FORM && !currentQuiz?.feedbackForm) {
      setShowExitModal(true)
    }
  }, [isStudentQuiz, contentValue, currentQuiz])

  const surveyType = useMemo(() => {
    let surveyTypeInt = Number(jsonFormData?.surveyType);
    switch (surveyTypeInt) {
      case 1:
        return SurveyTypes.ExitTickets;
      case 2:
        return SurveyTypes.Research;
      case 3:
        return SurveyTypes.PreProgram;
      case 4:
        return SurveyTypes.StudentQuiz;
      case 5:
        return SurveyTypes.PreProgram; // AU_PRE_PROGRAM_QUIZ
      case 6:
        return SurveyTypes.PostProgram; // POST_PROGRAM_QUIZ
      case 7:
        return SurveyTypes.AbsentStudent; // ABSENT_STUDENT
      default:
        return SurveyTypes.ExitTickets; // Default to Exit Tickets
    }
  }, [jsonFormData])

  const [createForm, { loading }] = useCreateFeedbackFormMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    },
    onCompleted(res) {
      setFeedbackForm(res.createFeedbackForm as FeedbackFormContent);
      countDownTimer.current = setInterval(() => {
        setTimeLeft(prevState => prevState - 60);
      }, 60 * 1000); // countdown every 10 seconds

      const _d = {
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(lesson),
        ...Events.mappers.content(contentValue),
        school_name: user?.schools?.[0]?.name || '',
        customer_name: user?.customer?.name || '',
        survey_type: surveyType,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };

      trackEvent(Events.actions.surveyFormLinkCreated, _d);
    }
  });

  useEffect(() => {
    if (hasNextContent && feedbackForm) {
      setTimeout(() => {
        setShowRememberNotify(true);
        setTimeout(() => {
          setShowRememberNotify(false);
        }, 15000)
      }, 500)
    }
  }, [feedbackForm, lesson, hasNextContent])

  const handleGenerateForm = useCallback(() => {
    const formId = getFormId(lesson?.id)
    if (formId) {   // if exist ExitTicket, cancel old one
      removeFormId(lesson?.id)
    }
    if (!exitTicket) {
      setTimeLeft(Expiration_time);
      createForm({
        variables: {
          input: {
            programName: formTypePreProgram ? null : program?.name,
            lessonName: lesson!.name,
            programId: formTypePreProgram ? null : program?.id,
            lessonId: lesson!.id,
            unitName: isHomeEDApp ? currentHeaUnit?.name : currentUnit?.name,
            unitId: isHomeEDApp ? currentHeaUnit?.id : currentUnit?.id,
            formData,
            grade: program?.name ? program?.name.replace(/\s*Program/, "") : '',
          }
        }
      });
      return;
    }
    setRegenerate(false)
    setShowExitModal(true);
  }, [
    Expiration_time, formTypePreProgram, createForm, getFormId, removeFormId, 
    currentHeaUnit?.id, currentHeaUnit?.name,
    currentUnit?.id, currentUnit?.name, exitTicket, formData, lesson, program
  ]);

  const handleCopyToClipboard = useCallback((text: string) => () => {
    window.navigator.clipboard.writeText(text)
    setShowConfirm(true)
    setIsActiveHovered(false)
    setTimeout(() => {
      setShowConfirm(false)
    }, 3000)
  }, [setShowConfirm]);

  const generateLink = useCallback(() => {
    setFeedbackForm(undefined);
    if (exitTicket) {
      setShowExitModal(true);
    } else {
      setTimeLeft(Expiration_time);
      handleGenerateForm();
    }
  }, [Expiration_time, exitTicket, handleGenerateForm]);

  const handleActiveFormPreview = useCallback((feedback: FeedbackFormContent, expiration: any, ticketCount?: any) => {
    if (isStudentQuiz) {
      setFeedbackForm(feedback);
      setShowExitModal(false);
      
      const updatedQuiz = {
        ...currentQuiz,
        feedbackForm: feedback,
        expiryMinutes: expiration * 60,
        status: true,
      }
      setCurrentQuiz(updatedQuiz as ExtendedQuiz)
      const updateQuizzes = myQuizzes.map((quiz: any, i: number) => {
        if (quiz.id === updatedQuiz?.id) {
          return {
            ...quiz,
            feedbackForm: feedback,
            expiryMinutes: expiration * 60,
            status: true,
          }
        } else {
          return quiz
        }
      })
      setMyQuizzes(updateQuizzes);
      return
    }
    const formId = getFormId(lesson?.id)
    if (formId) {   // if exist ExitTicket link, cancel old one
      cancelFeedbackForm({
        variables: {
          input: { id: formId }
        }
      })
    }
    const url = createURL(feedback.link ?? '');
    window.navigator.clipboard.writeText(url);
    successToast('Exit Ticket Link Successfully Created and Link copied successfully!');
    setFormId(lesson?.id, feedback.id)
    const tickets = exitTickets.map(ticket => {
      if (lesson?.number === ticket.lessonNum) {
        return {
          ...ticket,
          status: true,
          exitTicketCount: ticketCount,
          expiryMinutes: expiration * 60,
          copyLink: url,
          code: feedback.link ?? ''
        }
      }
      return ticket
    })
    setExitTickets(tickets)
    setShowExitModal(false);
  }, [lesson, exitTickets, setExitTickets, successToast, setFormId, getFormId, cancelFeedbackForm, isStudentQuiz, currentQuiz, setCurrentQuiz, myQuizzes, setMyQuizzes])

  const generateExitTicket = useCallback((e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    setRegenerate(true)
    setShowExitModal(true)
    setIsActiveHovered(false)
  }, [])

  const cancelExitTicket = useCallback((e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation()
    } else {
      e.stopPropagation()
    }
    setRegenerate(false)
    const formId = isStudentQuiz ? currentQuiz?.feedbackForm?.id : getFormId(lesson?.id)
    if (formId) {
      cancelFeedbackForm({
        variables: {
          input: { id: formId }
        }
      })
    }
    setIsActiveHovered(false);
  }, [lesson, getFormId, cancelFeedbackForm, isStudentQuiz, currentQuiz])

  const showExitTicketActive = (e: any) => {
    e.stopPropagation()
    setIsActiveHovered(current => !current);
  }

  const code = useMemo(() => {
    if (exitTicketActive) {
      return exitTicketActive.code
    }
    if (feedbackForm && feedbackForm?.link)
      return feedbackForm.link
    if (isStudentQuiz && currentQuiz) {
      return currentQuiz.feedbackForm?.link ?? ""
    }
    return ""
  }, [feedbackForm, exitTicketActive, isStudentQuiz, currentQuiz]);
  const codeList = code.split("");
  const shortLink = useMemo(() => (code ? `https://op.school/${code}` : ''), [code]);
  const longLink = createURL(code);

  const width = window.innerWidth
    || document.documentElement.clientWidth
    || document.body.clientWidth;

  const content = useMemo(() => {
    if (isStudentQuiz && currentQuiz?.expiryMinutes && currentQuiz?.feedbackForm && currentQuiz.expiryMinutes > 0) {
      const url = createURL(currentQuiz.feedbackForm.link ?? '');
      return (
        <div className={styles.wrapper} onClick={() => setIsActiveHovered(false)} ref={containerRef}>
          
          <div className={styles.mainContent}>
            <div className={styles.mainQuizTitle}>
              {'Active Assessment for: '} &nbsp;<span>{currentQuiz?.name}</span>
            </div>
            <div className={styles.qrContent}>
              {<div className={styles.hoverQrTipView} style={{ display: isQrHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Scan the QR code</div>
                <div className={styles.hoverTipDetail}>And answer the questions on your device</div>
              </div>}
              <div className={styles.qrCodeWrapper} onMouseOver={() => setIsQrHovered(true)} onMouseLeave={() => setIsQrHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <QRCode
                  value={url}
                  size={width > 800 ? 250 : width < 620 ? 140 : 180}
                  imageSettings={{ src: iconSrc, width: 24, height: 24, excavate: true }}
                  level={'M'}
                  renderAs={'svg'}
                />
              </div>
              <div className={styles.orView} >Or</div>
              {<div className={styles.hoverTipView} style={{ display: isLinkHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Instructions</div>
                <div className={styles.hoverTipDetail}>{(exitTicket && hasNextContent) ? "Students visit op.school in their browser and enter the code below." : "Send the link to your students. They can then paste the link in a web browser to answer the questions"}</div>
              </div>}
              <div className={styles.linkWrapper} onMouseOver={() => setIsLinkHovered(true)} onMouseLeave={() => setIsLinkHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <CopyToClipboard text={shortLink} onCopy={handleCopyToClipboard(shortLink)}>
                  <div className={styles.linkView}>
                    <div className={styles.visitText}>VISIT:</div>
                    <a className={styles.linkText} href='https://op.school' target="_blank" rel="noreferrer">https://op.school</a>
                    <div className={styles.codeTitle}>ENTER CODE:</div>
                    <div className={styles.codeView}>
                      {codeList.map((chr, index) => {
                        if (isNumber(chr)) {
                          return (
                            <span key={index} className={styles.numberCodeText}>{chr}</span>
                          )
                        } else {
                          return (
                            <span key={index} className={styles.codeText}>{chr}</span>
                          )
                        }
                      })
                      }
                    </div>
                  </div>
                </CopyToClipboard>
              </div>
            </div>
            <CopyToClipboard text={longLink} onCopy={handleCopyToClipboard(longLink)}>
              <div className={styles.copyLinkTitle} onMouseOver={() => setCopyLinkHovered(true)} onMouseLeave={() => setCopyLinkHovered(false)}>
                {'Share Link'}
              </div>
            </CopyToClipboard>
            <div className={styles.emailCopyTipView} style={{ display: copyLinkHovered ? "block" : "none" }}>
              <div className={styles.copyLinkMenuItem}>
                <strong>Click to copy</strong>&nbsp;{longLink}
              </div>
            </div>
          </div>
          <div className={styles.exitTicketBottomView}>
            <div className={styles.expiryInfoView}>
              <span>
                <strong>{Math.round((currentQuiz.expiryMinutes ?? 0) / 60)}</strong> Minutes Left
              </span>
              <span>
                <strong>{currentQuiz.feedbackForm?.exitTicketCount}</strong> Responses Max
              </span>
            </div>
            <div className={styles.activeShowView}>
              <Button disabled={canceling} size='small' className={styles.assessmentActiveButton} onClick={canceling ? () => { } : showExitTicketActive}
                leftIcon={canceling ? <Spinner width={20} height={20} /> : <TicketIcon />}
                label={'Link Active'} rightIcon={<WarningIcon width={20} height={21} className={styles.warningIcon} />}
              />
              <div className={styles.dropDownActiveMenu} style={{ display: isActiveHovered ? "block" : "none", width: '213px' }}>
                <div className={styles.activeInfoMenu} onClick={e => e.stopPropagation()}>
                  <OutlineIcon width={21} className={styles.outlineIcon} />
                  <div className={styles.activeInfo}>
                    <span>
                      <strong>{Math.round((currentQuiz?.expiryMinutes ?? 0) / 60)}</strong> Minutes Left
                    </span>
                    <span>
                      <strong>{currentQuiz.feedbackForm?.exitTicketCount}</strong> {'Responses Max'}
                    </span>
                  </div>
                </div>
                <div className={styles.dropDownMenuitem} onClick={cancelExitTicket}>
                  {'Cancel'}
                </div>
                <div className={styles.dropDownMenuitem} onClick={generateExitTicket}>
                  {'Regenerate'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (exitTicketActive?.status) {
      const url = exitTicketActive!.copyLink;
      return (
        <div className={styles.wrapper} onClick={() => setIsActiveHovered(false)} ref={containerRef}>
          <div className={styles.mainContent}>
            <div className={styles.mainTitle}>
              {name}
            </div>
            {/* {!!jsonFormData.instructions && <div className={styles.description}>{jsonFormData.instructions}</div>} */}
            <div className={styles.qrContent}>
              {<div className={styles.hoverQrTipView} style={{ display: isQrHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Scan the QR code</div>
                <div className={styles.hoverTipDetail}>And answer the questions on your device</div>
              </div>}
              <div className={styles.qrCodeWrapper} onMouseOver={() => setIsQrHovered(true)} onMouseLeave={() => setIsQrHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <QRCode
                  value={url}
                  size={width > 800 ? 250 : width < 620 ? 140 : 180}
                  imageSettings={{ src: iconSrc, width: 24, height: 24, excavate: true }}
                  level={'M'}
                  renderAs={'svg'}
                />
              </div>
              <div className={styles.orView} >Or</div>
              {<div className={styles.hoverTipView} style={{ display: isLinkHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Instructions</div>
                <div className={styles.hoverTipDetail}>{(exitTicket && hasNextContent) ? "Students visit op.school in their browser and enter the code below." : "Send the link to your students. They can then paste the link in a web browser to answer the questions"}</div>
              </div>}
              <div className={styles.linkWrapper} onMouseOver={() => setIsLinkHovered(true)} onMouseLeave={() => setIsLinkHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <CopyToClipboard text={shortLink} onCopy={handleCopyToClipboard(shortLink)}>
                  <div className={styles.linkView}>
                    <div className={styles.visitText}>VISIT:</div>
                    <a className={styles.linkText} href='https://op.school' target="_blank" rel="noreferrer">https://op.school</a>
                    <div className={styles.codeTitle}>ENTER CODE:</div>
                    <div className={styles.codeView}>
                      {codeList.map((chr, index) => {
                        if (isNumber(chr)) {
                          return (
                            <span key={index} className={styles.numberCodeText}>{chr}</span>
                          )
                        } else {
                          return (
                            <span key={index} className={styles.codeText}>{chr}</span>
                          )
                        }
                      })
                      }
                    </div>
                  </div>
                </CopyToClipboard>
              </div>
            </div>
            <CopyToClipboard text={longLink} onCopy={handleCopyToClipboard(longLink)}>
              <div className={styles.copyLinkTitle} onMouseOver={() => setCopyLinkHovered(true)} onMouseLeave={() => setCopyLinkHovered(false)}>
                {'Share Link'}
              </div>
            </CopyToClipboard>
            <div className={styles.emailCopyTipView} style={{ display: copyLinkHovered ? "block" : "none" }}>
              <div className={styles.copyLinkMenuItem}>
                <strong>Click to copy</strong>&nbsp;{longLink}
              </div>
            </div>
          </div>
          <div className={styles.exitTicketBottomView}>
            <div className={styles.expiryInfoView}>
              <span>
                <strong>{Math.round((exitTicketActive?.expiryMinutes ?? 0) / 60)}</strong> Minutes Left
              </span>
              <span>
                <strong>{exitTicketActive?.exitTicketCount}</strong> Responses Max
              </span>
            </div>
            <div className={styles.activeShowView}>
              <Button disabled={canceling} size='small' className={styles.exitTicketActiveButton} onClick={canceling ? () => { } : showExitTicketActive}
                leftIcon={canceling ? <Spinner width={20} height={20} /> : <TicketIcon />}
                label={formTypePreProgram ? 'Pre-Program Quiz Active' : formTypeExitTicket ? 'Exit Ticket Active' : 'Research Survey Active'} rightIcon={<WarningIcon width={20} height={21} className={styles.warningIcon} />}
              />
              <div className={styles.dropDownActiveMenu} style={{ display: isActiveHovered ? "block" : "none", width: formTypePreProgram ? '283px' : formTypeExitTicket ? '220px' : '270px' }}>
                <div className={styles.activeInfoMenu} onClick={e => e.stopPropagation()}>
                  <OutlineIcon width={21} className={styles.outlineIcon} />
                  <div className={styles.activeInfo}>
                    <span>
                      <strong>{Math.round((exitTicketActive?.expiryMinutes ?? 0) / 60)}</strong> Minutes Left
                    </span>
                    <span>
                      <strong>{exitTicketActive?.exitTicketCount}</strong> Responses Max
                    </span>
                  </div>
                </div>
                <div className={styles.dropDownMenuitem} onClick={cancelExitTicket}>
                  {'Cancel'}
                </div>
                <div className={styles.dropDownMenuitem} onClick={generateExitTicket}>
                  {'Regenerate'}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (!feedbackForm && !hasNextContent) {
      return (
        <div className={styles.generateContent} style={{ width: 'auto' }}>
          {
            <>
              <div className={exitTicket ? styles.exitTicketTitle : styles.qrLessonName}>{isStudentQuiz ? currentQuiz?.name + "'s Quiz" : name}</div>
              {exitTicket ? <div className={styles.exitTicketDescription}>{'Click the button to generate a Link for your students'}</div> :
                !!jsonFormData.instructions && <div className={styles.description}>{jsonFormData.instructions}</div>}
              {exitTicket ? <Button className={styles.exitTicketGenerateButton} onClick={handleGenerateForm}
                leftIcon={<TicketIcon width={33} height={33} />} label={formTypePreProgram ? 'GENERATE PRE-PROGRAM QUIZ' : formTypeExitTicket ? 'GENERATE EXIT TICKET' : isStudentQuiz ? 'GENERATE QUIZ' : 'GENERATE SURVEY'} /> :
                <Button className={styles.generateBtn} isLoading={loading} disabled={loading} label={'Continue'} onClick={handleGenerateForm} />}
            </>
          }
        </div>
      );
    } else if (!feedbackForm && hasNextContent) {
      return (
        <div className={styles.generateContent} style={{ width: 'auto' }}>
          <div className={exitTicket ? styles.exitTicketTitle : styles.qrLessonName}>{isStudentQuiz ? currentQuiz?.name + "'s Quiz" : name}</div>
          {exitTicket ? <div className={styles.exitTicketDescription}>{'Click the button to generate a Link for your students'}</div> :
            !!jsonFormData.instructions && <div className={styles.description}>{jsonFormData.instructions}</div>}
          {exitTicket ? <Button className={styles.exitTicketGenerateButton} onClick={handleGenerateForm}
            leftIcon={<TicketIcon width={33} height={33} />} label={formTypePreProgram ? 'GENERATE PRE-PROGRAM QUIZ' : formTypeExitTicket ? 'GENERATE EXIT TICKET' : isStudentQuiz ? 'GENERATE QUIZ' : 'GENERATE SURVEY'} /> :
            <Button className={styles.generateBtn} isLoading={loading} disabled={loading} label={'Continue'} onClick={handleGenerateForm} />}
        </div>
      );
    } else if (((exitTicket && exitTicketActive && exitTicketActive?.expiryMinutes > 0) || (!exitTicket && timeLeft > 0)) && feedbackForm) {
      const url = exitTicket ? exitTicketActive!.copyLink : createURL(feedbackForm.link ?? '');
      return (
        <div className={styles.wrapper} ref={containerRef}>
          <div className={styles.mainContent}>
            <div className={styles.mainTitle}>
              {name}
            </div>
            {/* {!!jsonFormData.instructions && <div className={styles.description}>{jsonFormData.instructions}</div>} */}
            <div className={styles.qrContent}>
              {<div className={styles.hoverQrTipView} style={{ display: isQrHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Scan the QR code</div>
                <div className={styles.hoverTipDetail}>And answer the questions on your device</div>
              </div>}
              <div className={styles.qrCodeWrapper} onMouseOver={() => setIsQrHovered(true)} onMouseLeave={() => setIsQrHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <QRCode
                  value={url}
                  size={width > 800 ? 250 : width < 620 ? 140 : 180}
                  imageSettings={{ src: iconSrc, width: 24, height: 24, excavate: true }}
                  level={'M'}
                  renderAs={'svg'}
                />
              </div>
              <div className={styles.orView} >Or</div>
              {<div className={styles.hoverTipView} style={{ display: isLinkHovered ? "block" : "none" }}>
                <div className={styles.hoverTipTitle}>Instructions</div>
                <div className={styles.hoverTipDetail}>{(exitTicket && hasNextContent) ? "Students visit op.school in their browser and enter the code below." : "Send the link to your students. They can then paste the link in a web browser to answer the questions"}</div>
              </div>}
              <div className={styles.linkWrapper} onMouseOver={() => setIsLinkHovered(true)} onMouseLeave={() => setIsLinkHovered(false)}>
                <div className={styles.iconView}>
                  <div className={styles.outlineIcon}>
                    <IconOutline />
                  </div>
                  <div className={styles.hoverOutlineIcon}>
                    <IconHoverOutline />
                  </div>
                </div>
                <CopyToClipboard text={shortLink} onCopy={handleCopyToClipboard(shortLink)}>
                  <div className={styles.linkView}>
                    <div className={styles.visitText}>VISIT:</div>
                    <div className={styles.linkText}>https://op.school</div>
                    <div className={styles.codeTitle}>ENTER CODE:</div>
                    <div className={styles.codeView}>
                      {codeList.map((chr, index) => {
                        if (isNumber(chr)) {
                          return (
                            <span key={index} className={styles.numberCodeText}>{chr}</span>
                          )
                        } else {
                          return (
                            <span key={index} className={styles.codeText}>{chr}</span>
                          )
                        }
                      })
                      }
                    </div>
                  </div>
                </CopyToClipboard>
              </div>

            </div>
            <CopyToClipboard text={longLink} onCopy={handleCopyToClipboard(longLink)}>
              <div className={styles.copyLinkTitle} onMouseOver={() => setCopyLinkHovered(true)} onMouseLeave={() => setCopyLinkHovered(false)}>
                {'Share Link'}
              </div>
            </CopyToClipboard>
            <div className={styles.emailCopyTipView} style={{ display: copyLinkHovered ? "block" : "none" }}>
              <div className={styles.copyLinkMenuItem}>
                <strong>Click to copy</strong>&nbsp;{longLink}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.generateContent}>
          <div className={styles.qrLessonName}>{isStudentQuiz ? 'Quiz Link Expired' : 'Survey Link Expired'}</div>
          <div className={styles.description}>{isStudentQuiz? 'Click button below to regenerate the Quiz QR Code.' : 'Click button below to regenerate the Survey QR Code.'}</div>
          <Button className={styles.reGenerateBtn} label={isStudentQuiz ? 'Regenerate Quiz QR Code' : 'Regenerate Survey QR Code'}
            onClick={generateLink} />
        </div>
      )
    }
  }, [
    width, copyLinkHovered, shortLink, longLink, codeList, formTypePreProgram, 
    formTypeExitTicket, exitTicketActive, feedbackForm, hasNextContent, exitTicket, 
    timeLeft, name, isActiveHovered, canceling, cancelExitTicket, generateExitTicket, 
    generateLink, isQrHovered, isLinkHovered, handleCopyToClipboard, jsonFormData.instructions, 
    loading, handleGenerateForm, isStudentQuiz, currentQuiz
  ]);

  return (
    <>
      <div className={styles.backdrop} onClick={() => setIsActiveHovered(false)} />
      {<div className={styles.confirmView} style={{ display: showConfirm ? "block" : "none" }}>
        <div className={styles.confirmDetail}>Link Copied Successfully! </div>
      </div>
      }
      {<div className={styles.notifySubView2} style={{ display: (showRememberNotify && !isStudentQuiz) ? "grid" : "none" }}>
        <InfoCircleIcon className={styles.infoCircle} />
        <div className={styles.notifyDetail}>
          <p>Please distribute the Exit Ticket Link to students</p>
        </div>
      </div>}
      {content}
      {showExitModal && <GenerateExitTicketLinkModal open={showExitModal} formData={formData} onClose={() => setShowExitModal(false)} onActiveExitTicket={handleActiveFormPreview} quizType={isStudentQuiz} currentQuiz={currentQuiz as Quiz} gradeName={program?.name ? program?.name.replace(/\s*Program/, "") : ''}/>}
    </>
  );
}

export default memo(FormGenerateContent);
